import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { ColorDot } from './Badges';
import { C, MONO, SHADOW } from './tokens';
import { OrderWorkflow, OrderWorkflowTask } from './useOrderWorkflow';

// 門市訂單的到貨，長得跟衣架地圖的卡片一樣，就放在「今天」的待辦裡。
// 出貨人員在收貨頁按「收到」之後，這些件數就出現在這裡；門市只按一次「收到了」，
// 系統自己把辦公室 → 門市 → 上架三段一次走完（Magnus 2026-09-22）。
export const arrivalQty = (task: OrderWorkflowTask) =>
  task.pending_office_qty + task.pending_transit_qty + task.pending_display_qty;
export const arrivalTasks = (tasks: OrderWorkflowTask[]) =>
  tasks.filter(task => !task.legacy_receipt && arrivalQty(task) > 0);

const Card: React.FC<{ task: OrderWorkflowTask; busy: boolean; onDone: (task: OrderWorkflowTask) => void }> =
  ({ task, busy, onDone }) => {
    const qty = arrivalQty(task);
    return (
      <Box
        component="article" data-arrival={task.line_id}
        sx={{
          border: '1px solid', borderColor: C.line, borderLeft: '4px solid', borderLeftColor: C.go,
          borderRadius: '10px', bgcolor: C.card, p: 1, boxShadow: SHADOW,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.25 }}>
          <Box sx={{ flex: 'none' }}>
            <ProductThumb
              target={{ sku: task.sku, title: task.title || task.sku, image_url: task.image_url || null, variant_id: task.variant_id }}
              width={84} height={112} radius={7}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="span"
                sx={{
                  fontFamily: MONO, fontSize: 10.5, fontWeight: 700, color: '#fff', bgcolor: C.go,
                  borderRadius: '4px', px: '5px', py: '2px', flex: 'none',
                }}
              >
                到
              </Box>
              <Typography component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }}>
                ×{qty}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', mt: 0.4 }}>
              <ColorDot name={task.color} />
              {task.color || task.title || task.sku}{task.size ? ` · ${task.size}` : ''}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: C.ink3, mt: 0.3 }}>
              {task.sku} · {task.title || ''}
            </Typography>
          </Box>
        </Box>
        <Box
          component="button" type="button" disabled={busy} onClick={() => onDone(task)}
          sx={{
            mt: 1, width: '100%', py: 1.15, cursor: busy ? 'default' : 'pointer', border: 0,
            bgcolor: C.go, color: '#fff', borderRadius: '9px', fontSize: 14, fontWeight: 800,
            opacity: busy ? 0.6 : 1,
          }}
        >
          收到了 · {qty} 件
        </Box>
      </Box>
    );
  };

const CounterArrivalCards: React.FC<{ workflow: OrderWorkflow; columns: number }> = ({ workflow, columns }) => {
  const { tasks, busy, pending, error, act, refresh } = workflow;
  const [failed, setFailed] = useState('');
  const rows = arrivalTasks(tasks);
  const confirm = async (task: OrderWorkflowTask) => {
    setFailed('');
    if (!await act(task, 'arrive', arrivalQty(task))) setFailed(error || '沒有存到，請再按一次。');
  };
  if (!rows.length) return null;
  return (
    <Box>
      {!!pending && (
        <Alert
          severity="warning" sx={{ mb: 1 }}
          action={<Button disabled={busy} onClick={() => {
            const task = tasks.find(t => t.line_id === pending.line_id);
            if (task) confirm(task); else refresh();
          }}>再試一次</Button>}
        >
          上一次「收到了」沒有確認到結果，再按一次會補送同一筆。
        </Alert>
      )}
      {!!failed && !pending && <Alert severity="error" sx={{ mb: 1 }}>{failed}</Alert>}
      <Box
        sx={{
          display: 'grid', gap: 1,
          ...(columns > 1
            ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
            : { '@media (min-width:600px)': { gridTemplateColumns: '1fr 1fr' } }),
        }}
      >
        {rows.map(task => <Card key={task.line_id} task={task} busy={busy || !!pending} onDone={confirm} />)}
      </Box>
    </Box>
  );
};
export default CounterArrivalCards;
